import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type ShapeVariant = "pill" | "rounded";

const shapeOrder: readonly ShapeVariant[] = ["rounded", "pill"];

interface ShapeClasses {
  bg: string;
  bgRadius: number;
  button: string;
  container: string;
  focusRing: string;
  input: string;
  item: string;
  otp: string;
  mergedBg: string;
  mergedRadius: number;
}

const shapeMap: Record<ShapeVariant, ShapeClasses> = {
  pill: {
    bg: "rounded-[20px]",
    bgRadius: 20,
    button: "rounded-[20px]",
    container: "rounded-3xl",
    focusRing: "rounded-[22px]",
    input: "rounded-[20px]",
    item: "rounded-[20px]",
    otp: "rounded-lg",
    mergedBg: "rounded-2xl",
    mergedRadius: 16,
  },
  rounded: {
    bg: "rounded-lg",
    bgRadius: 8,
    button: "rounded-lg",
    container: "rounded-xl",
    focusRing: "rounded-[10px]",
    input: "rounded-lg",
    item: "rounded-lg",
    otp: "rounded-md",
    mergedBg: "rounded-lg",
    mergedRadius: 8,
  },
};

interface ShapeContextValue {
  classes: ShapeClasses;
  setShape: (shape: ShapeVariant) => void;
  shape: ShapeVariant;
}

const ShapeContext = createContext<ShapeContextValue | null>(null);

function useShape(): ShapeClasses {
  return useContext(ShapeContext)?.classes ?? shapeMap.pill;
}

function useShapeContext(): ShapeContextValue {
  const context = useContext(ShapeContext);

  if (!context) {
    throw new Error("useShapeContext must be used within a ShapeProvider");
  }

  return context;
}

function transitionShape(callback: () => void) {
  const root = document.documentElement;
  root.classList.add("transitioning");
  void root.offsetHeight;
  callback();
  window.setTimeout(() => root.classList.remove("transitioning"), 200);
}

function ShapeProvider({
  children,
  defaultShape = "pill",
}: {
  children: ReactNode;
  defaultShape?: ShapeVariant;
}) {
  const [shape, setShapeState] = useState<ShapeVariant>(defaultShape);

  const setShape = useCallback((nextShape: ShapeVariant) => {
    transitionShape(() => setShapeState(nextShape));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "r" && event.key !== "R") {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName;
      if (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        target?.isContentEditable
      ) {
        return;
      }

      event.preventDefault();
      transitionShape(() => {
        setShapeState((current) => {
          const currentIndex = shapeOrder.indexOf(current);
          return shapeOrder[(currentIndex + 1) % shapeOrder.length] ?? "pill";
        });
      });
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <ShapeContext.Provider
      value={{
        classes: shapeMap[shape],
        setShape,
        shape,
      }}
    >
      {children}
    </ShapeContext.Provider>
  );
}

export { ShapeProvider, shapeMap, useShape, useShapeContext };
export type { ShapeClasses, ShapeVariant };
